import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Gamepad2, X } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { useAppStore } from '@/store/app'
import { useVoiceStore } from '@/store/voice'
import { useAchievementsStore } from '@/store/achievements'
import { releasesApi, type Release } from '@/lib/releasesApi'
import { pendingReleaseKey } from '@/lib/pendingRelease'
import { GAME_SERVERS, GAME_SERVERS_RELEASE_ID, gameServersSeen, rememberGameServers } from '@/lib/gameServersAnnouncement'
import styles from './GameServersAnnouncement.module.css'

function canShowNow() {
  return document.visibilityState === 'visible' && document.documentElement.dataset.keyboard !== 'true'
}

/** A launch notice consumes the same account receipt as What's new, on actual appearance. */
export function GameServersAnnouncement({ paused = false }: { paused?: boolean }) {
  const userId = useAuthStore((s) => s.user?.id)
  // Account-keyed child also resets an open card when switching accounts.
  return userId ? <AccountAnnouncement key={userId} userId={userId} paused={paused} /> : null
}

function AccountAnnouncement({ userId, paused }: { userId: string; paused: boolean }) {
  const queryKey = ['releases', 'game-servers', userId] as const
  const pending = useQuery({ queryKey, queryFn: () => releasesApi.pendingById(GAME_SERVERS_RELEASE_ID), staleTime: 5 * 60_000, refetchOnWindowFocus: true })
  const qc = useQueryClient()
  const inVoice = useVoiceStore((s) => s.connection !== null)
  const settings = useAppStore((s) => s.settings)
  const achievement = useAchievementsStore((s) => s.queue.length > 0)
  const [visible, setVisible] = useState(canShowNow)
  const [shown, setShown] = useState<Release | null>(null)
  const claimed = useRef(false)
  const blocked = paused || inVoice || settings !== null || achievement || !visible
  const { mutate: recordSeen } = useMutation({
    mutationFn: async (opened: boolean) => {
      // A retry must never mark a different signed-in account as having seen this notice.
      if (useAuthStore.getState().user?.id !== userId) return
      await releasesApi.seen(GAME_SERVERS_RELEASE_ID, opened)
    },
    retry: 2,
    onSuccess: () => {
      qc.setQueryData(['releases', 'game-servers', userId], { release: null })
      void qc.invalidateQueries({ queryKey: pendingReleaseKey(userId) })
    },
  })

  useEffect(() => {
    const update = () => setVisible(canShowNow())
    const observer = new MutationObserver(update)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-keyboard'] })
    document.addEventListener('visibilitychange', update)
    return () => { observer.disconnect(); document.removeEventListener('visibilitychange', update) }
  }, [])

  const release = pending.data?.release
  useEffect(() => {
    if (!release || release.id !== GAME_SERVERS_RELEASE_ID || blocked || claimed.current) return
    // Defer until the visible frame; cancel if a call, keyboard or modal opens meanwhile.
    const frame = requestAnimationFrame(() => {
      if (!canShowNow() || useAuthStore.getState().user?.id !== userId) return
      claimed.current = true
      if (!gameServersSeen(userId)) {
        setShown(release)
        rememberGameServers(userId)
      }
      // Recover a failed receipt on the next visit without showing the card again locally.
      recordSeen(false)
    })
    return () => cancelAnimationFrame(frame)
  }, [release, blocked, userId, recordSeen])

  if (!shown || blocked) return null
  return <GameServersAnnouncementCard title={shown.title} onClose={() => setShown(null)} onExplore={() => {
    recordSeen(true)
    setShown(null)
    useAppStore.getState().setView({ kind: 'discovery', openedAt: Date.now() })
  }} />
}

export function GameServersAnnouncementCard({ title, onClose, onExplore, preview = false }: {
  title: string; onClose: () => void; onExplore: () => void; preview?: boolean
}) {
  return (
    <aside className={`${styles.notice}${preview ? ` ${styles.preview}` : ''}`} aria-label="Игровые серверы">
      <button className={styles.close} type="button" aria-label="Закрыть плашку" onClick={onClose}><X size={18} /></button>
      <div className={styles.kicker}><Gamepad2 size={16} /> НОВОЕ В КЛИЧАТЕ</div>
      <h2>{title}</h2>
      <p>Найдите команду и общайтесь о любимых играх.</p>
      <div className={styles.games}>
        {GAME_SERVERS.map((game) => <img key={game.key} src={`/game-servers/${game.key}.png?v=2`} alt={game.name} width={38} height={38} />)}
        <span>{GAME_SERVERS.map(game => game.name).join(' · ')}</span>
      </div>
      <button className={styles.explore} type="button" onClick={onExplore}>Открыть каталог <ArrowRight size={17} /></button>
    </aside>
  )
}
